import React, { useState, useEffect } from 'react';
import { customersService, productsService } from '../services';
import LoadingSpinner from './LoadingSpinner';
import { formatCurrency, formatDate } from '../utils/formatters';

const CustomerDetail = ({ customer }) => {
  const [customerStats, setCustomerStats] = useState(null);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customer?.phone) {
      fetchCustomerData();
      fetchProducts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      const [stats, history] = await Promise.all([
        customersService.getStats(customer.phone),
        customersService.getHistory(customer.phone, { limit: 50 })
      ]);
      setCustomerStats(stats);
      setPurchaseHistory(history || []);
    } catch (err) {
      console.error('Error fetching customer data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productsService.getAll(1, 100);
      setProducts(response.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const getProductName = (productId) => {
    if (!productId) return 'Unknown Product';
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown Product';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (!customerStats) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Customer data not available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Customer Info Card */}
      <div className="bg-gray-50 rounded-xl p-4 md:p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm font-medium text-gray-500">Phone</div>
            <a href={`tel:${customerStats.phone}`} className="mt-1 block text-base md:text-lg font-semibold text-blue-600">{customerStats.phone}</a>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Location</div>
            <div className="mt-1 text-base md:text-lg font-semibold text-gray-900">{customerStats.location || '-'}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">First Purchase</div>
            <div className="mt-1 text-sm text-gray-900">
              {customerStats.firstPurchaseDate ? formatDate(customerStats.firstPurchaseDate) : '-'}
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Last Purchase</div>
            <div className="mt-1 text-sm text-gray-900">
              {customerStats.lastPurchaseDate ? formatDate(customerStats.lastPurchaseDate) : '-'}
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-3 md:p-4">
          <div className="text-sm font-medium text-gray-500">Total Purchases</div>
          <div className="mt-1 md:mt-2 text-lg md:text-2xl font-bold text-blue-600">{customerStats.totalPurchases}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 md:p-4">
          <div className="text-sm font-medium text-gray-500">Total Spent</div>
          <div className="mt-1 md:mt-2 text-lg md:text-2xl font-bold text-green-600">
            {formatCurrency(customerStats.totalSpent)}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 md:p-4">
          <div className="text-sm font-medium text-gray-500">Average Purchase</div>
          <div className="mt-1 md:mt-2 text-lg md:text-2xl font-bold text-purple-600">
            {formatCurrency(customerStats.averagePurchaseAmount)}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 md:p-4">
          <div className="text-sm font-medium text-gray-500">Purchase Frequency</div>
          <div className="mt-2 text-lg font-semibold text-gray-900">
            {customerStats.purchaseFrequency 
              ? `${customerStats.purchaseFrequency} days`
              : 'N/A'}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {customerStats.purchaseFrequencyLabel}
          </div>
        </div>
      </div>

      {/* Favorite Products */}
      {customerStats.favoriteProducts && customerStats.favoriteProducts.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Favorite Products</h3>
          <div className="space-y-3">
            {customerStats.favoriteProducts.map((product, index) => (
              <div key={product.productId || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {getProductName(product.productId)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {product.purchaseCount} {product.purchaseCount === 1 ? 'purchase' : 'purchases'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-green-600">
                    {formatCurrency(product.totalSpent)}
                  </div>
                  <div className="text-xs text-gray-500">Total spent</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Purchase History */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Purchase History</h3>
        {purchaseHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No purchase history available
          </div>
        ) : (
          <>
          <ul className="md:hidden divide-y divide-gray-100">
            {purchaseHistory.map((sale) => (
              <li key={sale.id} className="py-2.5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{getProductName(sale.productId)} × {sale.quantity || 1}</div>
                  <div className="text-xs text-gray-500">{formatDate(sale.date)}{sale.location ? ` · ${sale.location}` : ''}</div>
                </div>
                <div className="text-sm font-semibold text-emerald-600 tabular-nums shrink-0">{formatCurrency(sale.amount)}</div>
              </li>
            ))}
          </ul>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {purchaseHistory.map((sale) => (
                  <tr key={sale.id}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(sale.date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {getProductName(sale.productId)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                      {sale.quantity || 1}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-green-600 text-right">
                      {formatCurrency(sale.amount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {sale.location || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerDetail;

